/**
 * `/api/contacts` — contacts between WayAI accounts (gh #4661).
 *
 * A contact exists only after an invite, by exact email, is accepted. Every invite answers
 * the same "invite sent" row whether or not the email has an account, and that row keeps
 * one shape until it is accepted, cancelled or expires: it never carries the invitee's user
 * id, name or thread (Decisions 1 and 2). Identifiers carry a `people` prefix because
 * "contact" already names the outbound contacts of a hub (`outbound-contacts.ts`).
 */

import { z } from 'zod';
import { USER_ID_REGEX, timestampSchema, uuidSchema } from './common';

// =============================================================================
// LIMITS (#4661 Caps, Invites, Limits). Code constants: raising one is a code change.
// =============================================================================

/**
 * Contacts one user may hold, counted with their pending outgoing invites (each may still
 * become a contact). Checked when the user invites and when they accept, so only the user's
 * own concurrent requests can pass it.
 */
export const PEOPLE_CONTACTS_MAX = 5_000;
/** Pending incoming invites one user may hold; a new one past it is dropped silently. */
export const PEOPLE_CONTACT_INCOMING_INVITES_MAX = 100;
/** Pending outgoing invites one user may hold; a new invite past it answers 429. */
export const PEOPLE_CONTACT_OUTGOING_INVITES_MAX = 500;
/** Invites one user may send per UTC day; past it the invite answers 429. */
export const PEOPLE_CONTACT_INVITES_PER_DAY = 100;
/** Users one user may block; past it a block answers 429. */
export const PEOPLE_CONTACT_BLOCKS_MAX = 1_000;
/** Rows per page of every contacts list. */
export const PEOPLE_CONTACT_PAGE_MAX = 100;
export const PEOPLE_CONTACT_PAGE_DEFAULT = 50;
/** A pending invite is deleted this long after it was sent. */
export const PEOPLE_CONTACT_INVITE_TTL_DAYS = 30;
/** A decline suppresses re-invites from the same inviter for this long, silently. */
export const PEOPLE_CONTACT_DECLINE_SUPPRESSION_DAYS = 30;
/** RFC 5321's path limit, the longest address an invite accepts. */
export const PEOPLE_CONTACT_EMAIL_MAX_LENGTH = 254;

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

/** A WayAI user id (`USER_ID_REGEX`), bounded in length. */
export const peopleUserIdSchema = z.string().max(64).regex(USER_ID_REGEX, 'must be a user id');

/** An email address as an invite takes it: trimmed, at most RFC 5321's path limit. */
export const peopleInviteEmailSchema = z.string().trim().max(PEOPLE_CONTACT_EMAIL_MAX_LENGTH).email();

/** POST /api/contacts/invites */
export const peopleContactInviteBody = z
  .object({
    email: peopleInviteEmailSchema,
  })
  .strict();

/** DELETE /api/contacts/invites/outgoing/:inviteId, POST …/incoming/:inviteId/{accept,decline} */
export const peopleContactInviteIdParam = z.object({ inviteId: uuidSchema });

/** DELETE /api/contacts/:userId, DELETE /api/contacts/blocks/:userId */
export const peopleContactUserIdParam = z.object({ userId: peopleUserIdSchema });

/**
 * POST /api/contacts/blocks. `thread_id` names a thread the caller and `user_id` are both in —
 * a group, or a 1:1 — for a person the caller's own index holds no pair with yet (a group's
 * member who is not a contact): the route checks both belong to it before the block.
 */
export const peopleContactBlockBody = z.object({ user_id: peopleUserIdSchema, thread_id: uuidSchema.optional() }).strict();

/** GET /api/contacts, …/invites/outgoing, …/blocks — an opaque cursor and a page size. */
export const peopleContactPageQuery = z.object({
  cursor: z.string().min(1).max(256).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(PEOPLE_CONTACT_PAGE_MAX)
    .default(PEOPLE_CONTACT_PAGE_DEFAULT),
});

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================
//
// `display_name` is null when the person has set none; clients render "WayAI user", never
// the email's local part (Decision 11).

/**
 * One outgoing invite: exactly the email the inviter typed and when it was sent. Strict,
 * because this shape is Decision 2's whole promise — an extra field (a user id, a name, a
 * resolved flag) would tell the inviter whether the address has an account.
 */
export const peopleOutgoingInviteSchema = z
  .object({
    invite_id: uuidSchema,
    email: z.string(),
    status: z.literal('sent'),
    created_at: timestampSchema,
    expires_at: timestampSchema,
  })
  .strict();

export const peopleContactPersonSchema = z
  .object({
    user_id: peopleUserIdSchema,
    display_name: z.string().nullable(),
    email: z.string().nullable(),
  })
  .strict();

/** A contact. `thread_id` is the pair's 1:1 thread (`/api/threads/:threadId`). */
export const peopleContactSchema = peopleContactPersonSchema
  .extend({ contact_since: timestampSchema, thread_id: uuidSchema })
  .strict();

/** One incoming invite: who is asking, so the invitee can decide (Decision 11). */
export const peopleIncomingInviteSchema = z
  .object({
    invite_id: uuidSchema,
    inviter: peopleContactPersonSchema,
    created_at: timestampSchema,
    expires_at: timestampSchema,
  })
  .strict();

/**
 * A user the caller blocks. `deleted_user` is true once that account is deleted: its name and
 * email are erased, and clients show "Deleted user" (Decision 6).
 */
export const peopleBlockedUserSchema = peopleContactPersonSchema
  .extend({ blocked_at: timestampSchema, deleted_user: z.boolean() })
  .strict();

export type PeopleOutgoingInvite = z.infer<typeof peopleOutgoingInviteSchema>;
export type PeopleContact = z.infer<typeof peopleContactSchema>;
export type PeopleIncomingInvite = z.infer<typeof peopleIncomingInviteSchema>;
export type PeopleBlockedUser = z.infer<typeof peopleBlockedUserSchema>;

/** A cursor-paged list. `next_cursor` is null on the last page. */
export interface PeoplePage<T> {
  data: T[];
  next_cursor: string | null;
}

/** GET /api/contacts */
export type PeopleContactsResponse = PeoplePage<PeopleContact>;
/** GET /api/contacts/invites/incoming — capped at `PEOPLE_CONTACT_INCOMING_INVITES_MAX`, one page. */
export interface PeopleIncomingInvitesResponse {
  data: PeopleIncomingInvite[];
}
/** GET /api/contacts/invites/outgoing */
export type PeopleOutgoingInvitesResponse = PeoplePage<PeopleOutgoingInvite>;
/** GET /api/contacts/blocks */
export type PeopleBlockedUsersResponse = PeoplePage<PeopleBlockedUser>;
/** POST /api/contacts/invites — the same answer for every email ("invite sent"). */
export interface PeopleContactInviteResponse {
  data: PeopleOutgoingInvite;
}
/** POST /api/contacts/invites/incoming/:inviteId/accept */
export interface PeopleContactAcceptResponse {
  data: PeopleContact;
}
